import { AbyssIcon } from '@/assets/icons/AbyssIcon';
import * as state from '@/components/Analysis/state';
import { ES_MAPPING_KEYS } from '@/components/EsSheet/data';
import { Banner } from '@/components/shared/Banner';
import { COF_FIXED_VALID_VALUES, DC_FIXED_VALID_VALUES, EDD_FIXED_VALID_VALUES } from '@/constants';
import { abyssColors } from '@/theme/colors';
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  Grow,
  InputAdornment,
  MenuItem,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { process, State } from '@progress/kendo-data-query';
import { Grid, GridCellProps, GridColumn } from '@progress/kendo-react-grid';
import { useAtom } from 'jotai';
import { useEffect, useMemo, useState } from 'react';
import { useWizard } from '../context';
import { PanelHeader } from '../styles';
import { Exception, ExceptionType, RowData } from '../types';
import { ExceptionDropDown, ExceptionSearch, ExceptionsGrid } from './styles';

const getExceptionIcon = (type: ExceptionType) => {
  switch (type) {
    case 'duplicate':
      return <AbyssIcon iconName="file-copy-filled" />;
    case 'invalid_value':
      return <AbyssIcon iconName="alert-filled" />;
    case 'missing_required':
      return <AbyssIcon iconName="warning-filled" />;
    case 'type_error':
      return <AbyssIcon iconName="alert-filled" />;
    default:
      return <AbyssIcon iconName="checkbox-circle-fill" />;
  }
};

export const Exceptions = () => {
  const [editableEsSheet, setEditableEsSheet] = useAtom(state.editableEsSheet);
  const { goToNextStep, goToPreviousStep } = useWizard();
  const [gridData, setGridData] = useState<RowData[]>([]);
  const [selectedRows, setSelectedRows] = useState<Set<number>>(new Set());
  const [dataState, setDataState] = useState<State>({
    skip: 0,
    take: 6,
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [rowFilter, setRowFilter] = useState<'all' | 'ready' | 'skipped'>('all');
  const [exceptionFilter, setExceptionFilter] = useState<'all' | ExceptionType>('all');

  // Reset pagination when filters change
  useEffect(() => {
    setDataState((prev) => ({
      ...prev,
      skip: 0,
    }));
  }, [searchQuery, rowFilter, exceptionFilter]);

  const getValidValuesForColumn = (columnName: string): string[] | null => {
    if (columnName.includes('(COF)')) return COF_FIXED_VALID_VALUES;
    if (columnName === 'Damage Class') return DC_FIXED_VALID_VALUES;
    if (columnName === 'EDD Method') return EDD_FIXED_VALID_VALUES;
    return null;
  };

  const isNumericColumn = (columnName: string): boolean => {
    const numericColumns = [
      'Base POF Value',
      'Safety (POF)',
      'Environment (POF)',
      'Public Impact (POF)',
      'Direct Cost (POF)',
      'Business Loss (POF)',
      'Built Year',
      'Inspection Year',
      'Governing SHE ISO Risk',
      'Environment P1 (POF)',
      'Environment P2 (POF)',
      'Public Impact P1 (POF)',
      'Public Impact P2 (POF)',
      'Direct Cost P1 (POF)',
      'Direct Cost P2 (POF)',
      'Business Loss P1 (POF)',
      'Business Loss P2 (POF)',
    ];
    return numericColumns.some((col) => columnName.includes(col));
  };

  const detectExceptions = (
    data: string[][],
    headers: string[],
    mappings: Record<string, string>
  ): RowData[] => {
    const equipmentTagIndex = headers.findIndex((h) => {
      const targetKey = mappings[h];
      return targetKey === 'risk_equipment_tag';
    });

    const equipmentTags = new Map<string, number[]>();

    return data
      .map((row, rowIndex) => {
        const exceptions: Exception[] = [];
        const rowData: RowData = { id: rowIndex, exceptions };

        // Track equipment tags for duplicate detection
        if (equipmentTagIndex !== -1) {
          const tag = row[equipmentTagIndex];
          if (tag) {
            if (!equipmentTags.has(tag)) {
              equipmentTags.set(tag, []);
            }
            equipmentTags.get(tag)!.push(rowIndex);
          }
        }

        headers.forEach((header, colIndex) => {
          const cellValue = row[colIndex];
          const targetKey = mappings[header];
          const systemField = ES_MAPPING_KEYS.find((k) => k.key === targetKey);

          // Get mapped column name
          const mappedColumnName = systemField?.name || header;

          // Apply value mappings to get the final display value
          const valueMappings = editableEsSheet?.valueMappings?.[mappedColumnName];
          let finalValue = cellValue;
          if (valueMappings && valueMappings[cellValue]) {
            finalValue = valueMappings[cellValue];
            if (finalValue === '__RETAIN__') finalValue = cellValue;
          }

          // Store the mapped value for display
          rowData[mappedColumnName] = finalValue;

          // Check for missing required fields
          if (systemField?.required && (!cellValue || cellValue.trim() === '')) {
            exceptions.push({
              type: 'missing_required',
              message: `Required field is missing`,
              columnName: mappedColumnName,
              columnIndex: colIndex,
            });
          }

          // Check for invalid values
          if (cellValue && cellValue.trim() !== '') {
            const validValues = getValidValuesForColumn(mappedColumnName);

            // Validate against fixed values (N/A not included in FIXED_VALID_VALUES)
            if (validValues && !validValues.includes(finalValue)) {
              exceptions.push({
                type: 'invalid_value',
                message: `Invalid value "${finalValue}". Expected one of: ${validValues.join(
                  ', '
                )}`,
                columnName: mappedColumnName,
                columnIndex: colIndex,
              });
            }

            // Check for type errors (numeric columns, but skip 'N/A')
            if (
              isNumericColumn(mappedColumnName) &&
              finalValue !== 'N/A' &&
              isNaN(Number(finalValue))
            ) {
              exceptions.push({
                type: 'type_error',
                message: `Expected a number, got "${finalValue}"`,
                columnName: mappedColumnName,
                columnIndex: colIndex,
              });
            }
          }
        });

        return rowData;
      })
      .map((rowData, rowIndex) => {
        // Add duplicate exceptions after all rows are processed
        if (equipmentTagIndex !== -1) {
          const tag = data[rowIndex][equipmentTagIndex];
          if (tag && equipmentTags.get(tag)!.length > 1) {
            const mappedColumnName =
              ES_MAPPING_KEYS.find((k) => k.key === mappings[headers[equipmentTagIndex]])?.name ||
              headers[equipmentTagIndex];

            rowData.exceptions.push({
              type: 'duplicate',
              message: `Duplicate equipment tag "${tag}"`,
              columnName: mappedColumnName,
              columnIndex: equipmentTagIndex,
            });
          }
        }
        return rowData;
      });
  };

  useEffect(() => {
    if (!editableEsSheet) return;

    const mappings = editableEsSheet.mappings || {};

    // Apply inline edits to the raw data first, then detect exceptions
    const dataWithInlineEdits = editableEsSheet.data.map((row, rowIndex) => {
      const newRow = [...row];
      if (editableEsSheet.inlineEdits) {
        editableEsSheet.headers.forEach((header, colIndex) => {
          const editKey = `${rowIndex}:${colIndex}`;
          if (editableEsSheet.inlineEdits![editKey]) {
            newRow[colIndex] = editableEsSheet.inlineEdits![editKey];
          }
        });
      }
      return newRow;
    });

    // Now detect exceptions on the data with inline edits applied
    const processedData = detectExceptions(dataWithInlineEdits, editableEsSheet.headers, mappings);

    setGridData(processedData);

    // Automatically uncheck rows with exceptions
    const allIds = new Set(processedData.map((row) => row.id));
    const rowsWithExceptions = new Set(
      processedData.filter((row) => row.exceptions.length > 0).map((row) => row.id)
    );

    // Start with rows that have exceptions as excluded
    const autoExcluded = new Set(rowsWithExceptions);

    // If there are previously saved excludedRows, only keep them if they still have exceptions
    // or if they were manually unchecked (not auto-excluded due to exceptions)
    if (editableEsSheet.excludedRows && editableEsSheet.excludedRows.length > 0) {
      editableEsSheet.excludedRows.forEach((id) => {
        // Only keep the row excluded if it still has exceptions
        // This allows rows to be auto-selected when value mappings resolve their exceptions
        if (rowsWithExceptions.has(id)) {
          autoExcluded.add(id);
        }
      });
    }

    // Calculate included rows (all rows except excluded ones)
    const included = Array.from(allIds).filter((id) => !autoExcluded.has(id));
    setSelectedRows(new Set(included));

    // Persist the auto-excluded rows to editableEsSheet only if they changed
    const newExcludedRows = Array.from(autoExcluded);
    const currentExcludedRows = editableEsSheet.excludedRows || [];

    // Check if excludedRows actually changed to prevent infinite loop
    const hasChanged =
      newExcludedRows.length !== currentExcludedRows.length ||
      newExcludedRows.some((id) => !currentExcludedRows.includes(id));

    if (hasChanged && autoExcluded.size > 0) {
      setEditableEsSheet({
        ...editableEsSheet,
        excludedRows: newExcludedRows,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editableEsSheet]);

  // Get equipment tag column name for filtering
  const equipmentTagColumnName = useMemo(() => {
    if (!editableEsSheet) return 'Equipment Tag';
    const mappings = editableEsSheet.mappings || {};
    const equipmentTagHeader = editableEsSheet.headers.find((h) => {
      const targetKey = mappings[h];
      return targetKey === 'risk_equipment_tag';
    });
    if (equipmentTagHeader) {
      const targetKey = mappings[equipmentTagHeader];
      const systemField = ES_MAPPING_KEYS.find((k) => k.key === targetKey);
      return systemField?.name || equipmentTagHeader;
    }
    return 'Equipment Tag';
  }, [editableEsSheet]);

  // Apply filters to gridData while preserving original indices
  const filteredData = useMemo(() => {
    let filtered = [...gridData];

    // Apply search filter (equipment tag only)
    if (searchQuery.trim()) {
      filtered = filtered.filter((row) => {
        const equipmentTag = row[equipmentTagColumnName];
        return (
          typeof equipmentTag === 'string' &&
          equipmentTag.toLowerCase().includes(searchQuery.toLowerCase())
        );
      });
    }

    // Apply row filter (all, ready to import, skipped)
    if (rowFilter === 'ready') {
      filtered = filtered.filter((row) => selectedRows.has(row.id));
    } else if (rowFilter === 'skipped') {
      filtered = filtered.filter((row) => !selectedRows.has(row.id));
    }

    // Apply exception type filter
    if (exceptionFilter !== 'all') {
      filtered = filtered.filter((row) => {
        return row.exceptions.some((ex: Exception) => ex.type === exceptionFilter);
      });
    }

    return filtered;
  }, [gridData, searchQuery, rowFilter, exceptionFilter, selectedRows, equipmentTagColumnName]);

  const exceptionCount = useMemo(() => {
    return gridData.filter((row) => row.exceptions.length > 0).length;
  }, [gridData]);

  const exceptionCounts = useMemo(() => {
    const counts = {
      invalid_value: 0,
      duplicate: 0,
      missing_required: 0,
      type_error: 0,
    };

    // Count from all data, not affected by filters
    gridData.forEach((row) => {
      row.exceptions.forEach((exception: Exception) => {
        if (exception.type in counts) {
          counts[exception.type as keyof typeof counts]++;
        }
      });
    });

    return counts;
  }, [gridData]);

  const reviewStats = useMemo(() => {
    const totalRows = gridData.length;
    const excludedCount = Array.from(gridData.map((row) => row.id)).filter(
      (id) => !selectedRows.has(id)
    ).length;
    const toImport = totalRows - excludedCount;
    const toSkip = excludedCount;

    return {
      totalRows,
      toImport,
      toSkip,
    };
  }, [gridData, selectedRows]);

  const handleSelectRow = (id: number, checked: boolean) => {
    const newSelected = new Set(selectedRows);
    if (checked) {
      newSelected.add(id);
    } else {
      newSelected.delete(id);
    }
    setSelectedRows(newSelected);

    // Persist excluded rows (unchecked = excluded)
    if (editableEsSheet) {
      const allIds = new Set(gridData.map((row) => row.id));
      const excluded = Array.from(allIds).filter((rowId) => !newSelected.has(rowId));
      setEditableEsSheet({
        ...editableEsSheet,
        excludedRows: excluded,
      });
    }
  };

  const CheckboxCell = (props: GridCellProps) => {
    const isSelected = selectedRows.has(props.dataItem.id);

    return (
      <td style={{ textAlign: 'center' }}>
        <Checkbox
          checked={isSelected}
          onChange={(e) => handleSelectRow(props.dataItem.id, e.target.checked)}
          sx={{
            color: abyssColors.primary[300],
            '&.Mui-checked': {
              color: abyssColors.primary[600],
            },
          }}
        />
      </td>
    );
  };

  const ExceptionCell = (props: GridCellProps, isEquipmentTag: boolean) => {
    const { dataItem, field } = props;
    const exceptions = dataItem.exceptions.filter((e: Exception) => e.columnName === field);
    const hasException = exceptions.length > 0;
    const cellValue = props.dataItem[field || ''];
    const isInEdit = dataItem.inEdit === field;

    if (isInEdit) {
      return (
        <td style={{ position: 'relative' }}>
          <input
            type="text"
            value={cellValue || ''}
            onChange={(e) => {
              const updatedData = gridData.map((item) =>
                item.id === dataItem.id ? { ...item, [field || '']: e.target.value } : item
              );
              setGridData(updatedData);
            }}
            onBlur={(e) => {
              let newValue = e.target.value;

              // Convert to number if the column is numeric and the string is convertible to a valid number
              if (
                field &&
                isNumericColumn(field) &&
                newValue &&
                newValue.trim() !== '' &&
                !isNaN(Number(newValue))
              ) {
                newValue = String(Number(newValue));
              }

              // Persist inline edit to editableEsSheet on blur
              if (editableEsSheet && field) {
                const mappings = editableEsSheet.mappings || {};
                const sourceColumn = Object.keys(mappings).find((col) => {
                  const targetKey = mappings[col];
                  const systemField = ES_MAPPING_KEYS.find((k) => k.key === targetKey);
                  return systemField?.name === field;
                });

                if (sourceColumn) {
                  const columnIndex = editableEsSheet.headers.indexOf(sourceColumn);
                  const editKey = `${dataItem.id}:${columnIndex}`;

                  // Update editableEsSheet with the new value
                  const updatedEsSheet = {
                    ...editableEsSheet,
                    inlineEdits: {
                      ...editableEsSheet.inlineEdits,
                      [editKey]: newValue,
                    },
                  };

                  // Re-validate exceptions for the updated row
                  const rowIndex = dataItem.id;
                  const rowData = [...editableEsSheet.data[rowIndex]];
                  rowData[columnIndex] = newValue;

                  // Re-detect exceptions for this specific row
                  const rowExceptions: Exception[] = [];

                  editableEsSheet.headers.forEach((header, colIndex) => {
                    const cellValue =
                      colIndex === columnIndex
                        ? newValue
                        : editableEsSheet.inlineEdits?.[`${rowIndex}:${colIndex}`] ||
                          rowData[colIndex];
                    const targetKey = mappings[header];
                    const systemField = ES_MAPPING_KEYS.find((k) => k.key === targetKey);
                    const mappedColumnName = systemField?.name || header;

                    // Check for missing required fields
                    if (systemField?.required && (!cellValue || cellValue.trim() === '')) {
                      rowExceptions.push({
                        type: 'missing_required',
                        message: `Required field is missing`,
                        columnName: mappedColumnName,
                        columnIndex: colIndex,
                      });
                    }

                    // Check for invalid values
                    if (cellValue && cellValue.trim() !== '') {
                      const validValues = getValidValuesForColumn(mappedColumnName);
                      const valueMappings = editableEsSheet?.valueMappings?.[mappedColumnName];

                      let finalValue = cellValue;
                      if (valueMappings && valueMappings[cellValue]) {
                        finalValue = valueMappings[cellValue];
                        if (finalValue === '__RETAIN__') finalValue = cellValue;
                      }

                      if (validValues && !validValues.includes(finalValue)) {
                        rowExceptions.push({
                          type: 'invalid_value',
                          message: `Invalid value "${finalValue}". Expected one of: ${validValues.join(
                            ', '
                          )}`,
                          columnName: mappedColumnName,
                          columnIndex: colIndex,
                        });
                      }

                      if (
                        isNumericColumn(mappedColumnName) &&
                        finalValue !== 'N/A' &&
                        isNaN(Number(finalValue))
                      ) {
                        rowExceptions.push({
                          type: 'type_error',
                          message: `Expected a number, got "${finalValue}"`,
                          columnName: mappedColumnName,
                          columnIndex: colIndex,
                        });
                      }
                    }
                  });

                  // Check for duplicate equipment tags
                  const equipmentTagIndex = editableEsSheet.headers.findIndex((h) => {
                    const targetKey = mappings[h];
                    return targetKey === 'risk_equipment_tag';
                  });

                  if (equipmentTagIndex !== -1) {
                    const currentTag =
                      equipmentTagIndex === columnIndex
                        ? newValue
                        : editableEsSheet.inlineEdits?.[`${rowIndex}:${equipmentTagIndex}`] ||
                          editableEsSheet.data[rowIndex][equipmentTagIndex];

                    if (currentTag) {
                      let duplicateCount = 0;
                      editableEsSheet.data.forEach((row, idx) => {
                        const tag =
                          editableEsSheet.inlineEdits?.[`${idx}:${equipmentTagIndex}`] ||
                          row[equipmentTagIndex];
                        if (tag === currentTag) duplicateCount++;
                      });

                      if (duplicateCount > 1) {
                        const mappedColumnName =
                          ES_MAPPING_KEYS.find(
                            (k) => k.key === mappings[editableEsSheet.headers[equipmentTagIndex]]
                          )?.name || editableEsSheet.headers[equipmentTagIndex];
                        rowExceptions.push({
                          type: 'duplicate',
                          message: `Duplicate equipment tag "${currentTag}"`,
                          columnName: mappedColumnName,
                          columnIndex: equipmentTagIndex,
                        });
                      }
                    }
                  }

                  // If equipment tag was changed, re-validate all rows for duplicates
                  let finalUpdatedData;
                  if (equipmentTagIndex !== -1 && equipmentTagIndex === columnIndex) {
                    // Re-validate all rows since duplicate status may have changed
                    finalUpdatedData = gridData.map((item) => {
                      if (item.id === dataItem.id) {
                        return {
                          ...item,
                          [field]: newValue,
                          exceptions: rowExceptions,
                          inEdit: undefined,
                        };
                      }

                      // Re-check duplicates for other rows
                      const itemEquipmentTagValue =
                        editableEsSheet.inlineEdits?.[`${item.id}:${equipmentTagIndex}`] ||
                        editableEsSheet.data[item.id]?.[equipmentTagIndex];
                      const otherRowExceptions = item.exceptions.filter(
                        (e: Exception) => e.type !== 'duplicate'
                      );

                      // Check if this row now has a duplicate
                      if (itemEquipmentTagValue) {
                        let dupCount = 0;
                        editableEsSheet.data.forEach((row, idx) => {
                          const tag =
                            idx === rowIndex
                              ? newValue
                              : editableEsSheet.inlineEdits?.[`${idx}:${equipmentTagIndex}`] ||
                                row[equipmentTagIndex];
                          if (tag === itemEquipmentTagValue) dupCount++;
                        });

                        if (dupCount > 1) {
                          const mappedColName =
                            ES_MAPPING_KEYS.find(
                              (k) => k.key === mappings[editableEsSheet.headers[equipmentTagIndex]]
                            )?.name || editableEsSheet.headers[equipmentTagIndex];
                          otherRowExceptions.push({
                            type: 'duplicate',
                            message: `Duplicate equipment tag "${itemEquipmentTagValue}"`,
                            columnName: mappedColName,
                            columnIndex: equipmentTagIndex,
                          });
                        }
                      }

                      return {
                        ...item,
                        exceptions: otherRowExceptions,
                      };
                    });
                  } else {
                    // Only update the current row
                    finalUpdatedData = gridData.map((item) => {
                      if (item.id === dataItem.id) {
                        return {
                          ...item,
                          [field]: newValue,
                          exceptions: rowExceptions,
                          inEdit: undefined,
                        };
                      }
                      return item;
                    });
                  }

                  setGridData(finalUpdatedData);

                  // Update selected rows based on new exception state
                  const newSelected = new Set(selectedRows);
                  finalUpdatedData.forEach((item) => {
                    if (item.exceptions.length > 0) {
                      newSelected.delete(item.id);
                    } else if (item.id === dataItem.id && rowExceptions.length === 0) {
                      newSelected.add(item.id);
                    }
                  });

                  setSelectedRows(newSelected);

                  // Update excluded rows and editableEsSheet in one call
                  const allIds = new Set(finalUpdatedData.map((row) => row.id));
                  const excluded = Array.from(allIds).filter((rowId) => !newSelected.has(rowId));
                  setEditableEsSheet({
                    ...updatedEsSheet,
                    excludedRows: excluded,
                  });

                  return;
                }
              }

              // Fallback if no sourceColumn found
              const updatedData = gridData.map((item) =>
                item.id === dataItem.id ? { ...item, inEdit: undefined } : item
              );
              setGridData(updatedData);
            }}
            autoFocus
            style={{
              width: '100%',
              padding: '0.8rem',
              border: '0.1rem solid #ccc',
              borderRadius: '0.4rem',
              fontSize: '1.4rem',
              background: 'transparent',
            }}
          />
        </td>
      );
    }

    return (
      <td
        style={{
          backgroundColor: hasException ? abyssColors.error[50] : 'transparent',
          position: 'relative',
          cursor: 'pointer',
        }}
        onClick={() => {
          const updatedData = gridData.map((item) =>
            item.id === dataItem.id ? { ...item, inEdit: field } : { ...item, inEdit: undefined }
          );
          setGridData(updatedData);
        }}
      >
        <Stack direction="row" alignItems="center" gap={1}>
          {hasException && (
            <Tooltip
              title={
                <Stack gap={0.5}>
                  {exceptions.map((ex: Exception, idx: number) => (
                    <Typography key={idx} variant="caption">
                      {ex.message}
                    </Typography>
                  ))}
                </Stack>
              }
            >
              <span>{getExceptionIcon(exceptions[0].type)}</span>
            </Tooltip>
          )}
          <Typography
            key={hasException ? exceptions[0].message : cellValue}
            title={hasException ? exceptions[0].message : cellValue}
            sx={{
              cursor: 'pointer',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              display: 'block',
              maxWidth: '100%',
              fontWeight: 400,
              fontSize: '1.4rem',
              ...(isEquipmentTag ? {} : { color: abyssColors.primary[300] }),
            }}
          >
            {cellValue}
          </Typography>
        </Stack>
      </td>
    );
  };

  const ExceptionIndicatorCell = (props: GridCellProps) => {
    const exceptions = props.dataItem.exceptions || [];

    if (exceptions.length === 0)
      return (
        <td>
          <Stack direction="row" gap={0.5} alignItems="center" justifyContent="center">
            <AbyssIcon iconName="checkbox-circle-fill" />
          </Stack>
        </td>
      );

    const exceptionTypes = Array.from(new Set(exceptions.map((e: Exception) => e.type)));

    // Count exceptions by type for this row
    const exceptionCountsByType = exceptions.reduce(
      (acc: Record<ExceptionType, number>, ex: Exception) => {
        acc[ex.type] = (acc[ex.type] || 0) + 1;
        return acc;
      },
      {} as Record<ExceptionType, number>
    );

    // Get a user-friendly message for each exception type
    const getExceptionMessage = (type: ExceptionType, count: number) => {
      switch (type) {
        case 'invalid_value':
          return `${count} Invalid ${count === 1 ? 'value' : 'values'}`;
        case 'duplicate':
          return `${count} Duplication ${count === 1 ? 'error' : 'errors'}`;
        case 'missing_required':
          return `${count} Required ${count === 1 ? 'field' : 'fields'} missing`;
        case 'type_error':
          return `${count} Type ${count === 1 ? 'error' : 'errors'}`;
        default:
          return `${count} ${count === 1 ? 'Exception' : 'Exceptions'}`;
      }
    };

    return (
      <td>
        <Stack direction="row" gap={0.5} alignItems="center" justifyContent="center">
          {exceptionTypes.map((type) => {
            const count = exceptionCountsByType[type as ExceptionType] || 0;
            return (
              <Tooltip
                key={type as string}
                title={getExceptionMessage(type as ExceptionType, count)}
                placement="top"
              >
                <span>{getExceptionIcon(type as ExceptionType)}</span>
              </Tooltip>
            );
          })}
        </Stack>
      </td>
    );
  };

  const handleDataStateChange = (event: { dataState: State }) => {
    setDataState(event.dataState);
  };

  const handleItemChange = (event: { dataItem: RowData; field?: string; value?: string }) => {
    const updatedData = gridData.map((item) =>
      item.id === event.dataItem.id && event.field ? { ...item, [event.field]: event.value } : item
    );
    setGridData(updatedData);
  };

  if (!editableEsSheet) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="h5" color="error">
          No data available. Please upload a file first.
        </Typography>
      </Box>
    );
  }

  const mappings = editableEsSheet.mappings || {};
  const processedData = process(filteredData, dataState);
  const displayColumns = editableEsSheet.headers.map((header) => {
    const targetKey = mappings[header];
    const systemField = ES_MAPPING_KEYS.find((k) => k.key === targetKey);
    return systemField?.name || header;
  });

  return (
    <Stack width="100%" maxWidth="144rem" paddingBottom="6.4rem" gap="1.6rem">
      <Stack gap="3.2rem">
        <PanelHeader>
          <Typography variant="h3" fontWeight={500} color="primary.600">
            The file contains an "Exception"
          </Typography>
          <Typography variant="h5" fontWeight={400} color="primary.400">
            Add the values from your file to the allowed values in ES Tool
          </Typography>
        </PanelHeader>

        <Grow in={exceptionCount > 0} timeout={300} style={{ transitionDelay: '100ms' }}>
          <div>
            <Banner
              severity="warning"
              message="Some values need to be updated before you can continue."
            />
          </div>
        </Grow>

        <Box
          sx={{
            backgroundColor: abyssColors.primary[50],
            p: '2.4rem',
            borderRadius: '0.8rem',
          }}
        >
          <Stack gap="2.4rem">
            <Stack gap="1.6rem">
              <Typography variant="h4" fontWeight={600} color="primary.700">
                Exceptions
              </Typography>
              <Stack direction="row" gap="4rem" alignItems="center">
                <Stack direction="row" gap="1.2rem">
                  <Typography variant="caption" fontWeight={500} color="primary.500">
                    Total rows: {reviewStats.totalRows}
                  </Typography>
                  <Typography variant="caption" fontWeight={500} color="success.600">
                    Ready to import: {reviewStats.toImport}
                  </Typography>
                  <Typography variant="caption" fontWeight={500} color="error.600">
                    Skipped: {reviewStats.toSkip}
                  </Typography>
                </Stack>
                <Stack direction="row" gap="1.2rem">
                  <Typography variant="caption" fontWeight={500} color="primary.500">
                    Exceptions:
                  </Typography>
                  <Stack direction="row" gap={0.5} alignItems="center">
                    <AbyssIcon iconName="alert-filled" size="sm" />
                    <Typography variant="caption" color="primary.500">
                      Invalid values ({exceptionCounts.invalid_value + exceptionCounts.type_error})
                    </Typography>
                  </Stack>
                  <Stack direction="row" gap={0.5} alignItems="center">
                    <AbyssIcon iconName="file-copy-filled" size="sm" />
                    <Typography variant="caption" color="primary.500">
                      Duplication error ({exceptionCounts.duplicate})
                    </Typography>
                  </Stack>
                  <Stack direction="row" gap={0.5} alignItems="center">
                    <AbyssIcon iconName="warning-filled" size="sm" />
                    <Typography variant="caption" color="primary.500">
                      Missing value ({exceptionCounts.missing_required})
                    </Typography>
                  </Stack>
                </Stack>
              </Stack>
            </Stack>

            <Stack direction="row" gap="2.4rem" alignItems="center" marginTop="1.6rem">
              <Stack direction="row" gap="0.8rem" alignItems="center">
                <Typography variant="caption" color="primary.500">
                  Rows:
                </Typography>
                <FormControl size="small" sx={{ minWidth: '16rem' }}>
                  <ExceptionDropDown
                    value={rowFilter}
                    onChange={(e) => setRowFilter(e.target.value as 'all' | 'ready' | 'skipped')}
                    displayEmpty
                    sx={{
                      backgroundColor: 'white',
                    }}
                  >
                    <MenuItem value="all">All</MenuItem>
                    <MenuItem value="ready">Ready to import</MenuItem>
                    <MenuItem value="skipped">Skipped</MenuItem>
                  </ExceptionDropDown>
                </FormControl>
              </Stack>
              <Stack direction="row" gap="0.8rem" alignItems="center">
                <Typography variant="caption" color="primary.500">
                  Exceptions:
                </Typography>
                <FormControl size="small" sx={{ minWidth: '18rem' }}>
                  <ExceptionDropDown
                    value={exceptionFilter}
                    onChange={(e) => setExceptionFilter(e.target.value as 'all' | ExceptionType)}
                    displayEmpty
                    sx={{
                      backgroundColor: 'white',
                    }}
                  >
                    <MenuItem value="all">All exceptions</MenuItem>
                    <MenuItem value="type_error">Invalid values</MenuItem>
                    <MenuItem value="duplicate">Duplication error</MenuItem>
                    <MenuItem value="missing_required">Missing value</MenuItem>
                  </ExceptionDropDown>
                </FormControl>
              </Stack>
              <ExceptionSearch
                placeholder="Search by equipment tag"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                size="small"
                slotProps={{
                  input: {
                    endAdornment: (
                      <InputAdornment position="end">
                        <AbyssIcon iconName="search-simple" size="md" />
                      </InputAdornment>
                    ),
                  },
                }}
              />
            </Stack>

            <ExceptionsGrid>
              <Grid
                data={processedData}
                {...dataState}
                onDataStateChange={handleDataStateChange}
                onItemChange={handleItemChange}
                editable={true}
                pageable={{
                  buttonCount: 4,
                  info: true,
                  pageSizes: [6, 10, 25, 50],
                }}
              >
                <GridColumn
                  field="selected"
                  title="-"
                  width="66px"
                  cells={{ data: CheckboxCell }}
                  editable={false}
                  headerClassName="hide-name"
                />
                <GridColumn
                  field="exceptions"
                  title="-"
                  width="80px"
                  cells={{ data: ExceptionIndicatorCell }}
                  editable={false}
                  headerClassName="hide-name"
                />
                {displayColumns.map((columnName, index) => {
                  const isEquipmentTag = columnName === 'Equipment Tag';
                  return (
                    <GridColumn
                      key={index}
                      field={columnName}
                      title={columnName}
                      width="180px"
                      cells={{
                        data: (props: GridCellProps) => ExceptionCell(props, isEquipmentTag),
                      }}
                      editable={true}
                    />
                  );
                })}
              </Grid>

              <Stack
                direction="row"
                justifyContent="space-between"
                gap="0.8rem"
                // marginTop="0.8rem"
                // paddingLeft="3.2rem"
                sx={{
                  marginTop: '-2rem',
                  zIndex: 20,
                  position: 'relative',
                  width: 'fit-content',

                  paddingLeft: '3.2rem',
                }}
              >
                {/* <CustomPaginator
                  skip={dataState.skip ?? 0}
                  take={dataState.take ?? 6}
                  total={gridData.length}
                  pageSizes={[6, 10, 25, 50, 100]}
                  onPageChange={(event) => {
                    setDataState({
                      skip: event.page.skip,
                      take: event.page.take,
                    });
                  }}
                /> */}
              </Stack>
            </ExceptionsGrid>
          </Stack>
        </Box>
      </Stack>

      <Stack direction="row" justifyContent="flex-end" alignItems="center" width="100%">
        <Stack direction="row" gap={2}>
          <Button
            variant="outlined"
            onClick={goToPreviousStep}
            sx={{
              textTransform: 'none',
              borderColor: abyssColors.primary[300],
              color: abyssColors.primary[600],
              '&:hover': {
                borderColor: abyssColors.primary[400],
                backgroundColor: abyssColors.primary[50],
              },
            }}
          >
            Previous
          </Button>
          <Button
            variant="contained"
            onClick={goToNextStep}
            // disabled={exceptionCount > 0}
            sx={{
              textTransform: 'none',
            }}
          >
            Next
          </Button>
        </Stack>
      </Stack>
    </Stack>
  );
};
